// Bottleneck query (IMPLEMENTATION §0.2 R17). When the Gallery is STARVED, walk upstream from what feeds it
// (a Packer is passed through) to the Loader, then from the Loader's most-starved slot (fewest units; ties →
// slot order) to the machine producing that family, and on upstream. Name the first producer that is
// ≥ busyPermille busy over the readout window → "Upgrade X", or whose input is empty with no machine
// upstream to blame → "Feed X: needs {fam}" (a Smelter short of heat needs its fuel). A producer whose input
// is empty but is fed by another machine passes the question upstream to that machine.
// "Feeds" = the building's front points at the consumer, or a chute chain ends at the consumer and the
// chain's first chute takes from the building.

import { isMatcher, isProcessorType, recipeOf, slotFams } from '../bdefs.ts';
import { stepCell } from '../core/grid.ts';
import { floorDiv } from '../core/num.ts';
import type { Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { findBuilding } from '../entities.ts';
import type { BType, FamId } from '../gen/ids.ts';
import { isOpen, occAt } from '../occupancy.ts';
import { closedSum, windowTicks } from '../readout.ts';
import type { Building, ChuteB, GalleryB, PackerB, ProcessorB } from '../state.ts';
import { nextChute, topoOf } from '../topology.ts';
import { galleryStatus } from './galleryStatus.ts';

export type Bottleneck =
  | { k: 'upgrade'; bid: Int; type: BType }
  | { k: 'feed'; bid: Int; type: BType; fam: FamId };

const MAX_STEPS = 16;

/** Busy share of a machine over the closed readout window, in permille. */
export function busyPermille(c: Ctx, b: ProcessorB | PackerB): Int {
  return floorDiv(closedSum(c.d, b.busy, c.s.tick) * 1000, windowTicks(c.d));
}

function buildingAtCell(c: Ctx, x: Int, y: Int): Building | null {
  if (!isOpen(c.occ, x, y)) return null;
  const id = occAt(c.occ, x, y);
  return id > 0 ? findBuilding(c.s, id) : null;
}

function outputs(c: Ctx, b: Building, fam: FamId): boolean {
  if (b.ghost !== null) return false;
  if (b.type === 'packer') return b.lockFam === null || b.lockFam === fam;
  if (isProcessorType(b.type)) return recipeOf(c.d, b as ProcessorB).out === fam;
  return false;
}

/** The first chute of the chain that `ch` belongs to (walks back while a chute feeds it). */
function chainHead(c: Ctx, ch: ChuteB): ChuteB {
  let cur = ch;
  for (let i = 0; i < c.occ.cols * c.occ.rows; i++) {
    const back = stepCell(cur.x, cur.y, cur.inDir);
    const b = buildingAtCell(c, back.x, back.y);
    if (b === null || b.type !== 'chute') return cur;
    const nx = nextChute(c, b);
    if (nx === null || nx.id !== cur.id) return cur;
    cur = b;
  }
  return cur;
}

/** The machine that feeds `fam` into `target` (front-adjacent, or through a chute chain), or null. */
export function producerOf(c: Ctx, target: Building, fam: FamId): ProcessorB | PackerB | null {
  const topo = topoOf(c);
  const cell = target.y * c.occ.cols + target.x;
  for (const b of c.s.buildings) {
    if (b.id === target.id) continue;
    if (b.type === 'chute') {
      const ci = b.y * c.occ.cols + b.x;
      if (topo.sink[ci] !== cell) continue;
      const head = chainHead(c, b);
      const back = stepCell(head.x, head.y, head.inDir);
      const src = buildingAtCell(c, back.x, back.y);
      if (src === null || !outputs(c, src, fam)) continue;
      const f = stepCell(src.x, src.y, src.dir);
      if (f.x === head.x && f.y === head.y) return src as ProcessorB | PackerB;
      continue;
    }
    if (!outputs(c, b, fam)) continue;
    const f = stepCell(b.x, b.y, b.dir);
    if (f.x === target.x && f.y === target.y) return b as ProcessorB | PackerB;
  }
  return null;
}

function units(c: Ctx, xs: readonly { tier: Int }[]): Int {
  let n = 0;
  for (const r of xs) n += c.d.tierUnits[r.tier - 1] ?? 0;
  return n;
}

export function bottleneckOf(c: Ctx, g: GalleryB): Bottleneck | null {
  if (galleryStatus(c.d, g).binding !== 'starved') return null;
  const busyMin = c.d.economy.bottleneck.busyPermille;
  let node: ProcessorB | PackerB | null = producerOf(c, g, 'ammo');
  for (let step = 0; node !== null && step < MAX_STEPS; step++) {
    if (busyPermille(c, node) >= busyMin) return { k: 'upgrade', bid: node.id, type: node.type };
    if (node.type === 'packer') {
      node = producerOf(c, node, node.lockFam ?? 'ammo');
      continue;
    }
    const p: ProcessorB = node;
    const fams = slotFams(c.d, p);
    let slot = 0;
    if (isMatcher(p.type)) {
      for (let i = 1; i < p.slots.length; i++) {
        if (units(c, p.slots[i] ?? []) < units(c, p.slots[slot] ?? [])) slot = i;
      }
    } else if (p.type === 'smelter' && p.stall === 'NO_HEAT') {
      return { k: 'feed', bid: p.id, type: p.type, fam: c.d.buildings.smelter.fuel };
    } else if ((p.slots[0] ?? []).length > 0) {
      return null; // it has input and is not busy: the shortfall is not upstream of it
    }
    const fam = fams[slot] as FamId;
    const up = producerOf(c, p, fam);
    if (up === null) return { k: 'feed', bid: p.id, type: p.type, fam };
    node = up;
  }
  return null;
}
